import React, { useContext, useState, useEffect, useRef } from 'react';
import ScoringCategoryBadge from './ScoringCategoryBadge';
import { ScoringEventsContext } from './ScoringEventsContext';
import { ScorePopover } from './ScorePopover';
import { CodesetRow } from './CodesetRow';
import { findDeduction } from './finalCodeset.utils';
import { s } from './finalCodeset.styles';

export type FinalCodesetPanelProps = {
  idealCodes: CQA.ResolverCodeDto[] | null;
  isResolving: boolean;
  canViewScore: boolean;
  onReorder?: (fromIdx: number, toIdx: number) => void;
};

export function FinalCodesetPanel({
  idealCodes,
  isResolving,
  canViewScore,
  onReorder
}: FinalCodesetPanelProps): React.ReactElement | null {
  const latest = useContext(ScoringEventsContext);
  const [expanded, setExpanded] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const dragIdx = useRef<number | null>(null);
  const autoExpanded = useRef(false);
  const headerRef = useRef<HTMLDivElement>(null);

  // Auto-expand when ideal codes first appear; reset when they clear so the
  // next resolver result triggers auto-expand again.
  useEffect(() => {
    if (idealCodes == null || idealCodes.length === 0) {
      autoExpanded.current = false;
    } else if (!autoExpanded.current) {
      setExpanded(true);
      autoExpanded.current = true;
    }
  }, [idealCodes]);

  const codes = idealCodes ?? [];
  const hasAnyCodes = codes.length > 0;

  return (
    <div style={s.container}>
      <div
        ref={headerRef}
        style={s.header}
        onClick={() => {
          setExpanded(v => !v);
          if (showPopover) setShowPopover(false);
        }}>
        <span style={s.headerTitle}>
          Final Code Set
          {isResolving && (
            <span style={{ color: '#adb5bd', fontWeight: 400, marginLeft: 4 }}>…</span>
          )}
        </span>
        <div style={s.headerRight}>
          {canViewScore && latest && (
            <span
              style={{ cursor: 'pointer' }}
              onClick={e => {
                e.stopPropagation();
                if (!showPopover && headerRef.current) {
                  setAnchorRect(headerRef.current.getBoundingClientRect());
                }
                setShowPopover(v => !v);
              }}>
              <ScoringCategoryBadge value={latest.overallCategory} score={null} />
            </span>
          )}
          <span style={s.chevron}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {canViewScore && showPopover && latest && anchorRect && (
        <ScorePopover
          anchorRect={anchorRect}
          latest={latest}
          onClose={() => setShowPopover(false)}
        />
      )}

      {expanded && (
        <div style={s.body}>
          {idealCodes == null && isResolving && <span style={s.emptyText}>Computing…</span>}
          {idealCodes == null && !isResolving && <span style={s.emptyText}>No resolver.</span>}
          {idealCodes != null && !hasAnyCodes && <span style={s.emptyText}>Empty.</span>}
          {hasAnyCodes &&
            codes.map((c, idx) => (
              <CodesetRow
                key={`${c.codeType}-${c.id}`}
                code={c}
                isDragOver={dragOverIdx === idx}
                canViewScore={canViewScore}
                hasLiveScore={!!latest}
                entry={canViewScore && latest ? findDeduction(c, latest.breakdown) : undefined}
                onDragStart={() => {
                  dragIdx.current = idx;
                }}
                onDragOver={e => {
                  e.preventDefault();
                  setDragOverIdx(idx);
                }}
                onDragLeave={() => setDragOverIdx(null)}
                onDrop={e => {
                  e.preventDefault();
                  if (dragIdx.current != null && dragIdx.current !== idx) {
                    onReorder?.(dragIdx.current, idx);
                  }
                  dragIdx.current = null;
                  setDragOverIdx(null);
                }}
                onDragEnd={() => {
                  dragIdx.current = null;
                  setDragOverIdx(null);
                }}
              />
            ))}
        </div>
      )}
    </div>
  );
}

export default FinalCodesetPanel;
